import UserFullName from 'components/common/UserFullName';
import { Wrench } from 'lucide-react';
import React from 'react';
import { GrEdit } from 'react-icons/gr';
import { Calendar, Clock, UserIcon } from 'lucide-react';
import moment from 'moment';
import RepairOrder from 'models/RepairOrderModel';
import dateFormats from 'helpers/dateFormats';
import RepairOrderStatusType from 'models/types/RepaiOrderStatusTypes';
import DateTimePicker from 'components/common/DateTimePicker';

interface Props {
    repairOrder: RepairOrder;
    updateDueDate: (dueDate: string) => void;
    updateServiceWriter: (serviceWriterId: string) => void;
}

const RepairOrderDetails = ({ repairOrder, updateDueDate, updateServiceWriter }: Props) => {
    const [isDueDatePickerOpen, setIsDueDatePickerOpen] = React.useState(false);
    const [isServiceWriterPickerOpen, setIsServiceWriterPickerOpen] = React.useState(false);
    const [users, setUsers] = React.useState<{ id: string; name: string }[]>([]);

    const getROStatusBadge = (status: RepairOrderStatusType) => {
        const statusStyles = {
            [RepairOrderStatusType.Estimate]: 'bg-blue-100 text-blue-800',
            // [RepairOrderStatusType.Ready]: 'bg-yellow-100 text-yellow-800',
            // [RepairOrderStatusType.InProgress]: 'bg-purple-100 text-purple-800',
            // [RepairOrderStatusType.Completed]: 'bg-green-100 text-green-800',
            [RepairOrderStatusType.Invoice]: 'bg-green-100 text-green-800',
        };

        return (
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusStyles[status]}`}>
                {status === RepairOrderStatusType.Estimate && 'Estimate'}
                {/* {status === RepairOrderStatusType.Estimate && 'Ready to Start'}
                {status === RepairOrderStatusType.Estimate && 'In Progress'}
                {status === RepairOrderStatusType.Estimate && 'Completed'} */}
                {status === RepairOrderStatusType.Invoice && 'Invoice'}
            </span>
        );
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border border-gray-200">
            <div className="flex justify-between items-start mb-3">
                <h1 className="font-bold text-2xl text-green-600">RO #{repairOrder?.repairOrderNumber}</h1>

                {getROStatusBadge(repairOrder?.status)}
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Created: </span>
                    {moment(repairOrder?.createdAt).format(dateFormats.USA_DATETIME)}
                </div>
                <div className="flex items-center gap-2 justify-end">
                    <UserIcon className="w-4 h-4 text-gray-400" />
                    {!!repairOrder?.createdById && <UserFullName userId={repairOrder?.createdById} />}
                </div>
                <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-amber-600 font-medium flex">
                        Due:
                        <span>
                            {isDueDatePickerOpen ? (
                                <div className="ml-auto">
                                    <DateTimePicker
                                        onSave={updateDueDate}
                                        onCancel={() => setIsDueDatePickerOpen(false)}
                                        dateTime={repairOrder?.dueDate}
                                    />
                                </div>
                            ) : (
                                <div
                                    className="ml-auto font-bold text-black clickable flex items-baseline"
                                    onClick={() => setIsDueDatePickerOpen(true)}
                                >
                                    <span className="mr-1">
                                        {repairOrder?.dueDate ? (
                                            <span className="text-blue-500 underline">
                                                {moment(repairOrder.dueDate).format(dateFormats.USA_DATETIME)}
                                            </span>
                                        ) : (
                                            <span className="text-lg">-/-</span>
                                        )}
                                    </span>
                                    <GrEdit className="text-green-500 text-sm" />
                                </div>
                            )}
                        </span>
                    </span>
                </div>
                <div className="flex items-center gap-2 justify-end">
                    <Wrench className="w-4 h-4 text-gray-400" />
                    <span className="flex">
                        Service Writer:
                        <span>
                            {!isServiceWriterPickerOpen && !!repairOrder?.serviceWriterId && (
                                <div className="ml-auto font-bold text-blue-500 underline flex">
                                    <UserFullName userId={repairOrder?.serviceWriterId} />
                                    <GrEdit
                                        className="text-green-500 text-sm clickable"
                                        onClick={() => setIsServiceWriterPickerOpen(true)}
                                    />
                                </div>
                            )}
                            {isServiceWriterPickerOpen && (
                                <div
                                    id="dropdown"
                                    className={` ${
                                        !isServiceWriterPickerOpen && 'hidden'
                                    } w-full z-10 absolute  bg-white rounded divide-y divide-gray-100 shadow  border border-blue-500`}
                                >
                                    <ul className="py-1 text-sm text-gray-700" aria-labelledby="dropdownDefault">
                                        {users.map((user) => (
                                            <li
                                                className={`block clickable ${
                                                    user.id === repairOrder.serviceWriterId
                                                        ? 'bg-blue-400 text-white'
                                                        : 'hover:bg-gray-100'
                                                } py-2 px-4 font-semibold`}
                                                key={user.id}
                                                onClick={() => {
                                                    updateServiceWriter(user.id);
                                                }}
                                            >
                                                {user.name}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default RepairOrderDetails;
